/**
 * Export all styled components from a single file
 * This makes it easier to import components throughout the application
 * 
 * Usage:
 * import { Button, Card, FormInput } from '../styles/components';
 */

// Import all common components
import * as CommonComponents from './common';

// Import legacy components (will be gradually migrated to common)
import { Button as LegacyButton, PrimaryButton, SecondaryButton, IconButton as LegacyIconButton } from './Button';
import LegacyCard, { CardHeader as LegacyCardHeader, CardBody, CardFooter as LegacyCardFooter, CardTitle, CardSubtitle } from './Card';
import {
  FormGroup as LegacyFormGroup,
  FormLabel as LegacyFormLabel,
  FormInput as LegacyFormInput,
  FormSelect as LegacyFormSelect,
  FormTextarea,
  FormError,
  FormHint,
  FormCheckboxGroup as LegacyCheckboxGroup,
  FormCheckbox as LegacyCheckbox,
  FormRadioGroup as LegacyRadioGroup,
  FormRadio as LegacyRadio
} from './Form';
import { 
  CompanyCard,
  CompanyHeader,
  CompanyLogo,
  CompanyLogoLarge,
  CompanyInfo,
  CompanyName,
  CompanyIndustry,
  CompanySize,
  CompanyBody,
  CompanyDescription,
  InterestTagsContainer,
  InterestTag,
  CompanyFooter,
  MatchPercentBadge
} from './CompanyCard';
import {
  ModalOverlay as LegacyModalOverlay,
  ModalContent as LegacyModalContent,
  ModalHeader as LegacyModalHeader,
  ModalTitle as LegacyModalTitle,
  ModalSection,
  SectionTitle,
  CloseButton as LegacyCloseButton,
  ModalFooter as LegacyModalFooter,
  ActionButtons
} from './Modal';
import {
  NotificationBar,
  NotificationIcon,
  NotificationContent,
  NotificationTitle,
  NotificationMessage,
  NotificationCloseButton
} from './Notification';
import {
  Container as LegacyContainer,
  PageContainer,
  ContentWrapper,
  MainContent,
  Sidebar,
  PageHeader,
  PageTitle,
  PageSubtitle,
  Grid,
  GridItem,
  Flex,
  Divider,
  Section,
  SectionTitle as LayoutSectionTitle
} from './Layout';
import StatusBadge from './StatusBadge';
import Tag, { TagGroup } from './Tag';
import AuthFooter from './AuthFooter';
import { Badge, CountBadge } from './Badge';
import { ProgressBar, ProgressText } from './Progress';

// Extract common components for direct export
const {
  // Animations
  floatAnimation,
  pulseAnimation,
  shimmerAnimation,
  fadeInAnimation,
  scaleInAnimation,
  
  // Glassmorphism
  glassBase,
  glassMedium,
  glassDeep,
  glassPrimary,
  glassSuccess,
  glassWarning,
  glassError,
  glassHover,
  
  // Form components
  Input,
  Textarea,
  Select,
  Label,
  FormGroup,
  CheckboxGroup,
  Checkbox,
  RadioGroup,
  RadioOption,
  Radio,
  FormActions,
  ErrorMessage,
  disabledStyles,
  
  // Button components
  Button,
  // eslint-disable-next-line no-unused-vars
  SecondaryButton: CommonSecondaryButton,
  SuccessButton,
  WarningButton,
  DangerButton,
  OutlineButton,
  LinkButton,
  IconButton,
  loadingStyles,
  
  // Card components
  Card,
  CardWithHeader,
  CardHeader,
  CardFooter,
  InteractiveCard,
  CardGrid,
  StatsCard,
  EmptyStateCard,
  
  // Modal components
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter
} = CommonComponents;

// Export all components
// Create direct compatibility exports for components used elsewhere in the app
const Container = LegacyContainer;
const FormLabel = LegacyFormLabel;
const FormInput = LegacyFormInput;
const FormSelect = LegacyFormSelect;
const FormCheckbox = LegacyCheckbox;

export {
  // Compatibility exports for existing components
  Container,
  FormLabel,
  FormInput, 
  FormSelect,
  FormCheckbox,
  
  // Animation components
  floatAnimation,
  pulseAnimation,
  shimmerAnimation,
  fadeInAnimation,
  scaleInAnimation,
  
  // Glassmorphism components
  glassBase,
  glassMedium,
  glassDeep,
  glassPrimary,
  glassSuccess,
  glassWarning,
  glassError,
  glassHover,
  
  // Form components
  Input,
  Textarea,
  Select,
  Label,
  FormGroup,
  CheckboxGroup,
  Checkbox,
  RadioGroup,
  RadioOption,
  Radio,
  FormActions,
  ErrorMessage,
  disabledStyles,
  
  // Button components
  Button,
  SuccessButton,
  WarningButton,
  DangerButton,
  OutlineButton,
  LinkButton,
  IconButton,
  loadingStyles,
  
  // Card components
  Card,
  CardWithHeader,
  CardHeader,
  CardFooter,
  InteractiveCard,
  CardGrid,
  StatsCard,
  EmptyStateCard,
  
  // Modal components
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  ModalFooter,
  
  // Import-then-export compatibility components directly
  
  // Legacy components (will be gradually migrated)
  
  // Button - Legacy exports for backward compatibility
  LegacyButton,
  PrimaryButton,
  SecondaryButton,
  LegacyIconButton,
  
  // Card - Legacy exports for backward compatibility
  LegacyCard as CardLegacy,
  LegacyCardHeader as CardHeaderLegacy,
  CardBody,
  LegacyCardFooter as CardFooterLegacy,
  CardTitle,
  CardSubtitle,
  
  // Form - Legacy exports for backward compatibility
  LegacyFormGroup as FormGroupLegacy,
  LegacyFormLabel as FormLabelLegacy,
  LegacyFormInput as FormInputLegacy,
  LegacyFormSelect as FormSelectLegacy,
  FormTextarea,
  FormError,
  FormHint,
  LegacyCheckboxGroup as FormCheckboxGroupLegacy,
  LegacyCheckbox as FormCheckboxLegacy,
  LegacyRadioGroup as FormRadioGroupLegacy,
  LegacyRadio as FormRadioLegacy,
  
  // Company Card
  CompanyCard,
  CompanyHeader,
  CompanyLogo,
  CompanyLogoLarge,
  CompanyInfo,
  CompanyName,
  CompanyIndustry,
  CompanySize,
  CompanyBody,
  CompanyDescription,
  InterestTagsContainer,
  InterestTag,
  CompanyFooter,
  MatchPercentBadge,
  
  // Modal - Legacy exports for backward compatibility
  LegacyModalOverlay as ModalOverlayLegacy,
  LegacyModalContent as ModalContentLegacy,
  LegacyModalHeader as ModalHeaderLegacy,
  LegacyModalTitle as ModalTitleLegacy,
  ModalSection,
  SectionTitle,
  LegacyCloseButton as CloseButtonLegacy,
  LegacyModalFooter as ModalFooterLegacy,
  ActionButtons,
  
  // Notification
  NotificationBar,
  NotificationIcon,
  NotificationContent,
  NotificationTitle,
  NotificationMessage,
  NotificationCloseButton,
  
  // Layout
  LegacyContainer as ContainerLegacy,
  PageContainer,
  ContentWrapper,
  MainContent,
  Sidebar,
  PageHeader,
  PageTitle,
  PageSubtitle,
  Grid,
  GridItem,
  Flex,
  Divider,
  Section,
  LayoutSectionTitle,
  
  // Status Badge
  StatusBadge,
  
  // Tag
  Tag,
  TagGroup,
  
  // Auth
  AuthFooter,
  
  // Progress
  ProgressBar,
  ProgressText,
  
  // Badge
  Badge,
  CountBadge
};
